import { useGetVenteIdQuery } from "actions/ventes/queries";
import { useGetProduitsQuery } from "actions/produits/queries";
import { useGetCodesQuery, useGetEcrituresManuellesQuery } from "actions/comptabilite/queries";
import { useGetPiecesComptablesQuery } from "actions/pieces/queries";
import { useRattacherPieceMutation, useDetacherPieceMutation } from "actions/pieces/mutations";
import AutocompleteSelect from "components/AutocompleteSelect/AutocompleteSelect";
import FilePreview from "components/FilePreview/FilePreview";
import InputDetail from "components/Inputs/InputDetail";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import HeaderFirst from "utils/HeaderFirst";
import "./client.css";
import useVentesData from "./useVentesData";

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    return d.toLocaleDateString("fr-FR", options).replace(",", "");
  } catch (e) {
    return "";
  }
};

const DetailModifPage = () => {
  const { id } = useParams();
  const { clientID: dossier_id } = useParams();
  const navigate = useNavigate();

  const isManual = String(id || "").startsWith("MAN-");
  const manualId = isManual ? Number(String(id).replace("MAN-", "")) : null;

  const { data: ecrituresData, isLoading: isLoadingEcritures } = useGetEcrituresManuellesQuery(
    { dossier_id },
    { skip: !isManual || !dossier_id }
  );

  const {
    data: venteDetail,
    isLoading: isLoadingVente,
    error: venteError,
    refetch: refetchVente,
  } = useGetVenteIdQuery({ id, dossier_id }, { skip: !id || isManual });

  const { handleUpdateVente } = useVentesData(dossier_id);
  const { data: produitsData } = useGetProduitsQuery({ dossier_id });
  const { data: codesData } = useGetCodesQuery({ dossier_client: dossier_id });

  // EC-21 : Rattachement pièce GED
  const [selectedPieceId, setSelectedPieceId] = useState("");
  const { data: piecesData, refetch: refetchPieces } = useGetPiecesComptablesQuery({ dossier_id }, { skip: !dossier_id });
  const [rattacherPiece, { isLoading: isAttaching }] = useRattacherPieceMutation();
  const [detacherPiece, { isLoading: isDetaching }] = useDetacherPieceMutation();
  const piecesGed = piecesData?.documents || piecesData?.data || (Array.isArray(piecesData) ? piecesData : []);

  const manualEcriture = isManual
    ? (ecrituresData?.ecritures || []).find((e) => e.id === manualId)
    : null;

  const rawVente = venteDetail?.data?.[0] || venteDetail?.data || (Array.isArray(venteDetail) ? venteDetail[0] : venteDetail);

  const venteData = isManual
    ? (manualEcriture
        ? {
            id,
            intitule: manualEcriture.libelle_original || manualEcriture.libelle || "Écriture manuelle",
            description: manualEcriture.libelle || "",
            prix: Number(manualEcriture.debit || manualEcriture.credit || 0),
            montant_total: Number(manualEcriture.debit || manualEcriture.credit || 0),
            updated_at: manualEcriture.date,
            date_transaction: manualEcriture.date,
            client_details: { nom: "Saisie manuelle" },
            produits_associes: [],
          }
        : {})
    : (rawVente || {});

  const pieceRattachee =
    venteData?.piece_comptable_details ||
    piecesGed.find((p) => p.id === (venteData?.piece_comptable?.id || venteData?.piece_comptable));

  const handleRattacherPiece = async (pieceIdToAttach) => {
    if (isManual) {
      toast.info("Le rattachement direct s'effectue sur les opérations enregistrées en trésorerie.");
      return;
    }
    const pId = pieceIdToAttach || selectedPieceId;
    if (!pId) {
      toast.warning("Veuillez sélectionner une pièce dans la liste.");
      return;
    }
    const cleanPieceId = typeof pId === 'object' ? pId.id : Number(pId);
    try {
      const res = await rattacherPiece({ piece_id: cleanPieceId, target_type: "entree", target_id: id }).unwrap();
      toast.success(res?.message || "Pièce rattachée avec succès.");
      setSelectedPieceId("");
      if (refetchVente) await refetchVente();
      if (refetchPieces) await refetchPieces();
      window.dispatchEvent(new CustomEvent('compta:refresh'));
    } catch (err) {
      const msg = err?.data?.message || err?.data?.detail || err?.message || "Erreur lors du rattachement de la pièce.";
      toast.error("Erreur : " + msg);
    }
  };

  const handleDetacherPiece = async () => {
    if (isManual) return;
    const rawId = pieceRattachee?.id || (typeof venteData?.piece_comptable === 'object' ? venteData?.piece_comptable?.id : venteData?.piece_comptable);
    const pId = rawId ? Number(rawId) : undefined;
    try {
      const res = await detacherPiece({ piece_id: pId, target_type: "entree", target_id: id }).unwrap();
      toast.success(res?.message || "Pièce détachée.");
      if (refetchVente) await refetchVente();
      if (refetchPieces) await refetchPieces();
      window.dispatchEvent(new CustomEvent('compta:refresh'));
    } catch (err) {
      const msg = err?.data?.message || err?.data?.detail || err?.message || "Erreur lors du détachement de la pièce.";
      toast.error("Erreur : " + msg);
    }
  };

  // Comptes proposables pour une vente : classe 7 (produits) + 8 (HAO/cessions)
  const rawCodes = Array.isArray(codesData)
    ? codesData
    : codesData?.data || codesData?.results || [];
  const accountOptions = rawCodes
    .filter((c) => {
      const s = String(c.code ?? "");
      return s.startsWith("7") || s.startsWith("8");
    })
    .map((c) => ({ id: Number(c.code), nom: c.combine || `${c.code} - ${c.libelle}` }));

  const [updateData, setUpdateData] = useState({
    date: "",
    intitule: "",
    description: "",
    produits_associes: [],
  });
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  useEffect(() => {
    if (venteData && (venteData.id || venteData.intitule || venteData.prix !== undefined)) {
      const enrichedProduits = (venteData.produits_associes || []).map(
        (prodAssoc) => {
          return {
            id: prodAssoc.produit,
            nom: prodAssoc.nom || "Produit inconnu",
            quantite: prodAssoc.quantite != null ? prodAssoc.quantite.toString() : "",
            prix: prodAssoc.prix != null ? prodAssoc.prix.toString() : "",
            code_comptable: prodAssoc.code_comptable ?? null,
          };
        },
      );

      const totalVal = venteData.montant_total ?? venteData.prix ?? 0;

      setUpdateData({
        date: formatDate(venteData.updated_at || venteData.date_transaction || venteData.created_at),
        produits_associes: enrichedProduits,
        montant_total: String(totalVal),
        description: venteData.description || "",
        intitule: venteData.intitule || "",
      });
      setCalculatedTotal(Number(totalVal) || 0);
    }
  }, [venteDetail, manualEcriture]);

  // Recalculer le total automatiquement quand les produits changent
  useEffect(() => {
    if (updateData.produits_associes && updateData.produits_associes.length > 0) {
      const total = updateData.produits_associes.reduce((sum, prod) => {
        const quantite = parseFloat(prod.quantite) || 0;
        const prix = parseFloat(prod.prix) || 0;
        return sum + (quantite * prix);
      }, 0);
      setCalculatedTotal(total);
    } else {
      const totalVal = venteData?.montant_total ?? venteData?.prix ?? 0;
      setCalculatedTotal(Number(totalVal) || 0);
    }
  }, [updateData.produits_associes, venteData?.prix, venteData?.montant_total]);

  const handleQuantiteChange = (index, newQuantite) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].quantite = newQuantite.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handlePrixChange = (index, newPrix) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].prix = newPrix.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleProduitChange = (index, value) => {
    if (typeof value !== "number") return;
    const prod = (produitsData?.data || []).find((p) => p.id === value);
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        id: value,
        nom: prod?.nom || updatedProduits[index].nom,
        code_comptable:
          updatedProduits[index].code_comptable ?? prod?.code_comptable ?? null,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleCompteChange = (index, value) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        code_comptable: value,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isManual) {
      toast.info("Cette entrée est issue d'une écriture comptable. Modifiez-la dans la section Comptabilité.");
      navigate(-1);
      return;
    }
    const submitData = {
      ...updateData,
      prix: calculatedTotal,
      montant_total: calculatedTotal,
      produits_associes: (updateData.produits_associes || []).map((prod) => {
        const code = String(prod.code_comptable ?? "").replace(/\D/g, "");
        return {
          produit: prod.id,
          quantite: parseFloat(prod.quantite) || 0,
          prix: prod.prix ? prod.prix.toString() : "0",
          code_comptable: code ? Number(code) : null,
        };
      }),
    };
    await handleUpdateVente(id, submitData);
    toast.success("Mise à jour réussie. Tous les calculs comptables ont été mis à jour.");
    navigate(-1);
  };

  const nom = (
    <div style={{ display: "flex" }}>
      <h4 className="titre-dossier">
        Modification d&apos;entrée: {venteData?.id || id || "Détails"}
      </h4>
    </div>
  );

  const isLoading = isManual ? isLoadingEcritures : isLoadingVente;
  const isError = !isManual && venteError;

  if (isLoading) {
    return (
      <div style={{ padding: "30px", background: "#ffffff", borderRadius: "12px", margin: "20px" }}>
        Chargement des détails de l&apos;entrée...
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: "30px", background: "#ffffff", borderRadius: "12px", margin: "20px", color: "#dc2626" }}>
        Une erreur est survenue lors du chargement de l&apos;entrée.
      </div>
    );
  }

  if (!venteData || (!venteData.id && !venteData.intitule && venteData.prix === undefined)) {
    return (
      <div style={{ padding: "30px", background: "#ffffff", borderRadius: "12px", margin: "20px" }}>
        Entrée introuvable ou non disponible.
      </div>
    );
  }

  return (
    <div className="vente-detail-container">
      <div className="d-flex align-items-center">
        <HeaderFirst
          nom={nom}
          description={
            <div>Modifiez les informations de cette entrée. Tous les calculs comptables seront mis à jour automatiquement.</div>
          }
        />
      </div>
      <form
        className="form-layout"
        onSubmit={onSubmit}
        style={{ padding: "15px" }}
      >
        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Client"
              name="client"
              disabled
              value={
                venteData?.client_details?.nom ||
                venteData?.clientNom ||
                venteData?.client_nom ||
                (typeof venteData?.client === "string" ? venteData.client : "") ||
                "Non spécifié"
              }
              className={"custom-input"}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Date"
              name="date"
              type="text"
              disabled
              value={updateData.date}
              className={"custom-input"}
            />
          </div>
        </div>

        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Intitulé"
              name="intitule"
              value={updateData.intitule}
              onChange={(e) =>
                setUpdateData({ ...updateData, intitule: e.target.value })
              }
              className={"custom-input"}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Description"
              name="description"
              value={updateData.description}
              onChange={(e) =>
                setUpdateData({ ...updateData, description: e.target.value })
              }
              className={"custom-input"}
            />
          </div>
        </div>

        {updateData.produits_associes &&
        updateData.produits_associes.length > 0 ? (
          updateData.produits_associes.map((prod, index) => (
            <div key={`${prod.id}-${index}`} className="row flex-row justify-content-between" style={{ marginBottom: "15px" }}>
              <div className="col-4">
                <AutocompleteSelect
                  label="Produit"
                  options={produitsData?.data || []}
                  value={prod.id}
                  onChange={(value) => handleProduitChange(index, value)}
                  placeholder="Sélectionnez un produit"
                />
              </div>
              <div className="col-2">
                <InputDetail
                  label="Quantité"
                  name={`quantite-${prod.id}`}
                  type="number"
                  value={prod.quantite}
                  onChange={(e) =>
                    handleQuantiteChange(index, parseFloat(e.target.value) || 0)
                  }
                  className={"custom-input"}
                />
              </div>
              <div className="col-3">
                <InputDetail
                  label="Prix"
                  name={`prix-${prod.id}`}
                  type="number"
                  value={prod.prix}
                  onChange={(e) => handlePrixChange(index, parseFloat(e.target.value) || 0)}
                  className={"custom-input"}
                />
              </div>
              <div className="col-3">
                <AutocompleteSelect
                  label="Compte comptable"
                  options={accountOptions}
                  value={prod.code_comptable ?? null}
                  onChange={(value) => handleCompteChange(index, value)}
                  createText="Utiliser"
                  onCreateNew={async (name) => {
                    const digits = String(name).replace(/\D/g, "");
                    if (!digits) return null;
                    return { id: Number(digits), nom: String(name).trim() };
                  }}
                  placeholder="Ex: 706000 (vide = compte du produit)"
                />
              </div>
            </div>
          ))
        ) : (
          <p>Aucun produit associé.</p>
        )}

        {/* EC-21 : Rattachement pièce GED */}
        <div className="pieceContainer col-md-12 mb-3">
          <p style={{ color: "#009688", fontWeight: "bold" }} className="form-label">
            Pièce justificative (GED)
          </p>
          {pieceRattachee ? (
            <div className="card py-2" style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8 }}>
              <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <span style={{ fontWeight: 700, color: "#166534", marginRight: 8 }}>📎 Pièce liée :</span>
                  <strong>{pieceRattachee.titre || pieceRattachee.title}</strong>
                  {pieceRattachee.partenaire ? ` : ${pieceRattachee.partenaire}` : ""}
                  {pieceRattachee.montant ? ` : ${Number(pieceRattachee.montant).toLocaleString("fr-FR")} FCFA` : ""}
                  {(pieceRattachee.fileUrl || pieceRattachee.fichier) && (
                    <a
                      href={pieceRattachee.fileUrl || pieceRattachee.fichier}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ms-3 badge text-white"
                      style={{ background: "#009688", color: "#ffffff", textDecoration: "none", padding: "6px 12px", borderRadius: 12 }}
                    >
                      <span style={{ color: '#ffffff', fontWeight: 600 }}>Ouvrir le fichier</span>
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ fontWeight: 600, padding: "5px 14px", borderRadius: "20px" }}
                  disabled={isDetaching}
                  onClick={handleDetacherPiece}
                >
                  ✕ {isDetaching ? "Détachement..." : "Détacher"}
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <select
                className="form-select"
                style={{ flex: 1, minWidth: 260 }}
                value={selectedPieceId}
                onChange={(e) => setSelectedPieceId(e.target.value)}
              >
                <option value="">Sélectionner une pièce de la GED...</option>
                {piecesGed.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.titre || p.title} : {p.partenaire || "N/A"} : {Number(p.montant || 0).toLocaleString("fr-FR")} FCFA
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  background: "#009688",
                  borderColor: "#009688",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  padding: "7px 18px",
                  borderRadius: "20px",
                }}
                disabled={!selectedPieceId || isAttaching}
                onClick={() => handleRattacherPiece(selectedPieceId)}
              >
                📎 {isAttaching ? "Rattachement..." : "Rattacher"}
              </button>
            </div>
          )}
        </div>

        {/* Aperçu de la pièce jointe */}
        <div className="input-row d-flex">
          {(() => {
            const rawPreview = pieceRattachee?.fileUrl || pieceRattachee?.fichier || venteData?.piece_joint || venteData?.justificatif_file;
            const previewPath = typeof rawPreview === 'string' ? rawPreview : (rawPreview?.url || null);
            if (previewPath) {
              return (
                <div className="pieceContainer col-md-12 mb-3">
                  <p style={{ color: "#009688", fontWeight: "bold" }} className="form-label">
                    Aperçu de la pièce jointe
                  </p>
                  <a href={previewPath} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                    <div className="card py-3">
                      <div className="card-body d-flex align-items-center justify-content-center text-center">
                        <FilePreview filePath={previewPath} />
                      </div>
                    </div>
                  </a>
                </div>
              );
            }
            return (
              <div className="pieceContainer col-md-12 mb-3">
                <p style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>
                  Aucune pièce justificative jointe pour cette transaction.
                </p>
              </div>
            );
          })()}
        </div>

        <div className="form-navigation-buttons">
          <button type="submit" className="next-button">
            Modifier
          </button>
        </div>
      </form>
    </div>
  );
};

export default DetailModifPage;
